import { useEffect, useRef, useState } from 'react';
import { OptionRow } from '../components/Controls.tsx';
import { api } from '../lib/api.ts';
import { errorMessage } from '../../shared/errors.ts';
import type { CodexAccount, CodexLoginState } from '../../shared/api.ts';

export function CodexLogin({ onChanged }: { onChanged: () => void }) {
  const [account, setAccount] = useState<CodexAccount | null>(null);
  const [session, setSession] = useState<CodexLoginState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issue, setIssue] = useState<string | null>(null);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  const alive = useRef(true);

  async function readAccount() {
    const d = await api.get<{ account: CodexAccount; issue?: string }>('/api/settings/codex/account');
    if (!alive.current) return;
    setAccount(d.account);
    setIssue(d.issue || null);
  }

  useEffect(() => {
    alive.current = true;
    void readAccount().catch((e) => {
      if (!alive.current) return;
      setAccount({ connected: false });
      setError(`Cannot check the account: ${errorMessage(e)}. Try Connect ChatGPT again.`);
    });
    void api
      .get<{ session: CodexLoginState | null }>('/api/settings/codex/login')
      .then((d) => {
        if (alive.current) setSession(d.session);
      })
      .catch((e) => alive.current && setError(errorMessage(e)));
    return () => {
      alive.current = false;
    };
  }, []);

  const running = session?.status === 'running';
  const sessionId = session?.id;
  useEffect(() => {
    if (!running || !sessionId) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    let failures = 0;
    async function poll() {
      try {
        const d = await api.get<{ session: CodexLoginState | null }>(
          `/api/settings/codex/login?id=${encodeURIComponent(sessionId!)}`,
        );
        if (stopped) return;
        failures = 0;
        setError(null);
        setSession(d.session);
        if (!d.session || d.session.status !== 'running') {
          // Notify before awaiting: setting a terminal session stops this effect.
          onChangedRef.current();
          void readAccount().catch((e) => {
            if (alive.current) setError(`Cannot check the account: ${errorMessage(e)}. Reopen Settings to retry.`);
          });
          return;
        }
      } catch (e) {
        if (stopped) return;
        if (++failures >= 3) setError(`Cannot check sign-in: ${errorMessage(e)}. Reopen Settings to reconnect.`);
      }
      if (!stopped) timer = setTimeout(() => void poll(), 1000);
    }
    timer = setTimeout(() => void poll(), 500);
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [running, sessionId]);

  async function connect() {
    setBusy(true);
    setError(null);
    try {
      const d = await api.post<{ session: CodexLoginState }>('/api/settings/codex/login', {});
      if (alive.current) setSession(d.session);
    } catch (e) {
      if (alive.current) setError(errorMessage(e));
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  async function cancel() {
    if (!session) return;
    setBusy(true);
    setError(null);
    try {
      const d = await api.post<{ session: CodexLoginState | null }>('/api/settings/codex/login/cancel', {
        id: session.id,
      });
      if (alive.current) setSession(d.session);
      await readAccount();
      if (alive.current) onChangedRef.current();
    } catch (e) {
      if (alive.current) setError(errorMessage(e));
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  async function disconnect() {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/settings/codex/logout', {});
      if (alive.current) setSession(null);
      await readAccount();
      if (alive.current) onChangedRef.current();
    } catch (e) {
      if (alive.current) setError(errorMessage(e));
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  return (
    <>
      <OptionRow
        label="ChatGPT account"
        stack
        hint={
          'One account is shared by everyone using Codex in this Studio. Builds and chat edits use its Codex allowance. Connecting takes effect immediately.'
        }
      >
        <div className="cred-stack">
          <p className="small" aria-live="polite">
            {account === null
              ? 'Checking account…'
              : account.connected
                ? `Connected${account.email ? ` as ${account.email}` : ''}${account.planType ? ` · ${account.planType}` : ''}`
                : 'No ChatGPT account connected.'}
          </p>
          <div className="cred">
            {running ? (
              <button className="btn btn--sm btn--ghost" disabled={busy} onClick={cancel}>
                Cancel sign-in
              </button>
            ) : (
              <>
                <button className="btn btn--sm" disabled={busy || account === null} onClick={connect}>
                  {busy ? 'Please wait…' : account?.connected ? 'Reconnect ChatGPT' : 'Connect ChatGPT'}
                </button>
                {account?.connected && (
                  <button className="btn btn--sm btn--ghost" disabled={busy} onClick={disconnect}>
                    Disconnect
                  </button>
                )}
              </>
            )}
          </div>
          {issue && (
            <p className="small error">
              {issue} Try Connect ChatGPT again, or ask the Studio maintainer to check the Codex installation.
            </p>
          )}
          {error && (
            <p className="small error" role="alert">
              {error}
            </p>
          )}
          {session?.status === 'error' && (
            <p className="small error" role="alert">
              {session.error}
            </p>
          )}
          {session?.status === 'done' && <p className="small muted">ChatGPT sign-in completed.</p>}
        </div>
      </OptionRow>
      {running && (
        <OptionRow
          label="Authorise Studio"
          stack
          hint="Enable device code login in ChatGPT → Settings → Security if asked. Enter this code on the OpenAI page; Studio will detect when you finish."
        >
          {session?.userCode && session.verificationUrl ? (
            <div className="cred">
              <code className="cred__hint">{session.userCode}</code>
              <a className="btn btn--sm" href={session.verificationUrl} target="_blank" rel="noreferrer">
                Open ChatGPT sign-in
              </a>
            </div>
          ) : (
            <p className="small muted" role="status">
              Getting a sign-in code…
            </p>
          )}
        </OptionRow>
      )}
    </>
  );
}
